import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const ADMIND_LOGO = "https://admindaiacademy.s3.eu-central-1.amazonaws.com/Random+Sheet/Loghi+AdMind/ADMINDLogoNegative.png";

const AGENT_STEP_ICONS = ["🎯", "✍️", "🖼️", "📅", "📊", "✅"];

const FEATURE_META = [
    { icon: "🤖", color: "#701280", badge: "NEW" },
    { icon: "💼", color: "#0A66C2" },
    { icon: "✍️", color: "#FF6B35" },
    { icon: "🖼️", color: "#00BFA5" },
    { icon: "🎬", color: "#E91E63" },
    { icon: "📅", color: "#FF9800" },
];

const STATS_VALUES = ["<5 min", "6", "10+", "1"];

const keyframes = `
@keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-12px); }
}
@keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(112,18,128,0.3), 0 0 60px rgba(112,18,128,0.1); }
    50% { box-shadow: 0 0 40px rgba(112,18,128,0.6), 0 0 100px rgba(112,18,128,0.2); }
}
@keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
}
@keyframes slide-up {
    0% { opacity: 0; transform: translateY(30px); }
    100% { opacity: 1; transform: translateY(0); }
}
@keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
}
@keyframes orbit {
    0% { transform: rotate(0deg) translateX(180px) rotate(0deg); }
    100% { transform: rotate(360deg) translateX(180px) rotate(-360deg); }
}
@keyframes spin-slow {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
@keyframes fade-in-up {
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
}
@keyframes gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}
`;

function RotatingWord() {
    const { t } = useTranslation();
    const words = t('landing.rotating_words', { returnObjects: true });
    const [index, setIndex] = useState(0);
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        let timeoutId = null;
        const interval = setInterval(() => {
            setVisible(false);
            timeoutId = setTimeout(() => {
                setIndex(i => (i + 1) % words.length);
                setVisible(true);
            }, 350);
        }, 2200);
        return () => {
            clearInterval(interval);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [words.length]);

    return (
        <Box component="span" sx={{
            display: 'inline-block',
            background: 'linear-gradient(135deg, #701280, #FF6B35, #E91E8C)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'shimmer 3s linear infinite',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(10px)',
            transition: 'opacity 0.35s ease, transform 0.35s ease',
            minWidth: 340,
        }}>
            {words[index]}
        </Box>
    );
}

function AgentTypingDemo() {
    const { t } = useTranslation();
    const stepLabels = t('landing.agent_steps', { returnObjects: true });
    const AGENT_STEPS = stepLabels.map((label, i) => ({ icon: AGENT_STEP_ICONS[i], label }));

    const [stepIndex, setStepIndex] = useState(0);
    const [text, setText] = useState('');
    const [typing, setTyping] = useState(true);

    useEffect(() => {
        const full = AGENT_STEPS[stepIndex].label;
        let i = 0;
        let nextStepTimeout = null;
        setText('');
        setTyping(true);
        const typeInterval = setInterval(() => {
            i++;
            setText(full.slice(0, i));
            if (i >= full.length) {
                clearInterval(typeInterval);
                setTyping(false);
                nextStepTimeout = setTimeout(() => {
                    setStepIndex(s => (s + 1) % AGENT_STEPS.length);
                }, 1400);
            }
        }, 38);
        return () => {
            clearInterval(typeInterval);
            if (nextStepTimeout) clearTimeout(nextStepTimeout);
        };
    }, [stepIndex]);

    return (
        <Box sx={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(112,18,128,0.25)',
            borderRadius: '16px',
            p: 3,
            backdropFilter: 'blur(20px)',
            minHeight: 80,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#00ff88', animation: 'pulse-glow 2s infinite' }} />
                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: 2, textTransform: 'uppercase' }}>
                    {t('landing.agent_running')}
                </Typography>
            </Box>
            {AGENT_STEPS.map((step, i) => (
                <Box key={i} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    opacity: i < stepIndex ? 1 : i === stepIndex ? 1 : 0.2,
                    transition: 'opacity 0.4s ease',
                }}>
                    <Typography sx={{ fontSize: '1rem', flexShrink: 0 }}>{step.icon}</Typography>
                    <Typography sx={{
                        fontSize: '0.82rem',
                        color: i < stepIndex ? 'rgba(255,255,255,0.5)' : i === stepIndex ? '#fff' : 'rgba(255,255,255,0.2)',
                        fontFamily: 'monospace',
                        textDecoration: i < stepIndex ? 'line-through' : 'none',
                    }}>
                        {i === stepIndex ? text : step.label}
                        {i === stepIndex && typing && (
                            <Box component="span" sx={{ borderRight: '2px solid #701280', ml: 0.3, animation: 'blink 1s infinite' }}>&nbsp;</Box>
                        )}
                        {i < stepIndex && (
                            <Box component="span" sx={{ ml: 1, color: '#00ff88', fontSize: '0.75rem' }}>✓</Box>
                        )}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function FeatureCard({ feature, delay }) {
    const [hovered, setHovered] = useState(false);
    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                background: hovered ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${hovered ? feature.color + '60' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: '20px',
                p: 3,
                cursor: 'default',
                transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                transform: hovered ? 'translateY(-4px)' : 'none',
                boxShadow: hovered ? `0 20px 60px ${feature.color}20` : 'none',
                position: 'relative',
                overflow: 'hidden',
                animation: `fade-in-up 0.6s ease forwards`,
                animationDelay: `${delay}ms`,
                opacity: 0,
            }}
        >
            {hovered && (
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                    background: `linear-gradient(90deg, transparent, ${feature.color}, transparent)`,
                }} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{
                    width: 48, height: 48, borderRadius: '14px',
                    background: `linear-gradient(135deg, ${feature.color}30, ${feature.color}10)`,
                    border: `1px solid ${feature.color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.5rem', flexShrink: 0,
                }}>
                    {feature.icon}
                </Box>
                {feature.badge && (
                    <Chip
                        label={feature.badge}
                        size="small"
                        sx={{
                            background: feature.badge === 'NEW' ? 'linear-gradient(135deg, #701280, #E91E8C)' : 'rgba(255,255,255,0.1)',
                            color: '#fff',
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            height: 20,
                            letterSpacing: 0.5,
                        }}
                    />
                )}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff', mb: 0.8 }}>{feature.title}</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>{feature.desc}</Typography>
        </Box>
    );
}

export default function Landing() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const heroRef = useRef(null);

    const statsLabels = t('landing.stats', { returnObjects: true });
    const STATS = STATS_VALUES.map((value, i) => ({ value, label: statsLabels[i] }));

    const translatedFeatures = t('landing.features', { returnObjects: true });
    const FEATURES = translatedFeatures.map((f, i) => ({ ...FEATURE_META[i], ...f }));

    const steps = t('landing.steps', { returnObjects: true });
    const STEPS_META = [
        { step: '01', icon: '💬' },
        { step: '02', icon: '⚡' },
        { step: '03', icon: '🎯' },
    ];
    const HOW_STEPS = steps.map((s, i) => ({ ...STEPS_META[i], ...s }));

    const footerLinks = [
        t('landing.footer_privacy'),
        t('landing.footer_terms'),
        t('landing.footer_contact'),
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <Box sx={{
            minHeight: '100vh',
            background: '#080810',
            color: '#fff',
            overflowX: 'hidden',
            fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        }}>
            <style>{keyframes}</style>

            {/* Navbar */}
            <Box sx={{
                position: 'fixed',
                top: 0, left: 0, right: 0,
                zIndex: 100,
                py: 1.5,
                px: { xs: 2, md: 6 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: scrolled ? 'rgba(8,8,16,0.85)' : 'transparent',
                backdropFilter: scrolled ? 'blur(20px)' : 'none',
                borderBottom: scrolled ? '1px solid rgba(112,18,128,0.2)' : 'none',
                transition: 'all 0.3s ease',
            }}>
                <img src={ADMIND_LOGO} alt="Admind" style={{ height: 36 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LanguageSwitcher dark={true} />
                    <Button
                        onClick={() => navigate('/login')}
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            '&:hover': { color: '#fff' },
                        }}
                    >
                        {t('common.login')}
                    </Button>
                    <Button
                        onClick={() => navigate('/registration')}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #701280, #8E3A9E)',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            borderRadius: '10px',
                            px: 2.5,
                            boxShadow: '0 4px 20px rgba(112,18,128,0.4)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #8E3A9E, #701280)',
                                boxShadow: '0 6px 30px rgba(112,18,128,0.6)',
                            },
                        }}
                    >
                        Sign Up
                    </Button>
                </Box>
            </Box>

            {/* Background orbs */}
            <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 0 }}>
                <Box sx={{
                    position: 'absolute', top: '-20%', left: '-10%',
                    width: 700, height: 700,
                    background: 'radial-gradient(circle, rgba(112,18,128,0.18) 0%, transparent 70%)',
                    borderRadius: '50%', filter: 'blur(40px)',
                }} />
                <Box sx={{
                    position: 'absolute', top: '10%', right: '-15%',
                    width: 600, height: 600,
                    background: 'radial-gradient(circle, rgba(255,107,53,0.1) 0%, transparent 70%)',
                    borderRadius: '50%', filter: 'blur(40px)',
                }} />
                <Box sx={{
                    position: 'absolute', bottom: '10%', left: '30%',
                    width: 500, height: 500,
                    background: 'radial-gradient(circle, rgba(112,18,128,0.12) 0%, transparent 70%)',
                    borderRadius: '50%', filter: 'blur(60px)',
                }} />
            </Box>

            {/* ===== HERO ===== */}
            <Box ref={heroRef} sx={{
                position: 'relative', zIndex: 1,
                minHeight: '100vh',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                textAlign: 'center',
                px: { xs: 2, md: 4 }, pt: 12, pb: 8,
            }}>
                {/* Badge */}
                <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    background: 'rgba(112,18,128,0.15)', border: '1px solid rgba(112,18,128,0.4)',
                    borderRadius: '100px', px: 2, py: 0.75, mb: 4,
                    animation: 'slide-up 0.8s ease forwards',
                }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#00ff88', animation: 'pulse-glow 2s infinite' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600 }}>
                        {t('landing.badge')}
                    </Typography>
                </Box>

                {/* Main headline */}
                <Typography sx={{
                    fontSize: { xs: '2.8rem', sm: '4rem', md: '5.5rem', lg: '6.5rem' },
                    fontWeight: 900, lineHeight: 1.05, letterSpacing: '-2px', mb: 2,
                    animation: 'slide-up 0.9s ease forwards', animationDelay: '0.1s', opacity: 0,
                }}>
                    {t('landing.hero_title1')}<br />
                    <Box component="span" sx={{
                        background: 'linear-gradient(135deg, #701280 0%, #C850C0 50%, #FF6B35 100%)',
                        backgroundSize: '200% auto', WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent', animation: 'shimmer 4s linear infinite',
                    }}>
                        {t('landing.hero_title2')}
                    </Box>
                </Typography>

                {/* Rotating subtitle */}
                <Typography sx={{
                    fontSize: { xs: '1.1rem', md: '1.6rem' }, color: 'rgba(255,255,255,0.6)', mb: 1,
                    animation: 'slide-up 1s ease forwards', animationDelay: '0.2s', opacity: 0,
                }}>
                    {t('landing.hero_subtitle')}
                </Typography>
                <Typography sx={{
                    fontSize: { xs: '1.3rem', md: '1.8rem' }, fontWeight: 700, mb: 5,
                    animation: 'slide-up 1s ease forwards', animationDelay: '0.25s', opacity: 0, minHeight: 44,
                }}>
                    <RotatingWord />
                </Typography>

                {/* CTAs */}
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mb: 3,
                    animation: 'slide-up 1s ease forwards', animationDelay: '0.35s', opacity: 0,
                }}>
                    <Button
                        onClick={() => navigate('/registration')}
                        variant="contained" size="large"
                        sx={{
                            background: 'linear-gradient(135deg, #701280, #C850C0)', color: '#fff',
                            textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                            borderRadius: '14px', px: 5, py: 1.6,
                            boxShadow: '0 8px 40px rgba(112,18,128,0.5)', animation: 'pulse-glow 3s infinite',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #C850C0, #701280)',
                                transform: 'translateY(-2px)', boxShadow: '0 12px 50px rgba(112,18,128,0.7)',
                            },
                            transition: 'all 0.25s ease',
                        }}
                    >
                        {t('landing.cta_primary')}
                    </Button>
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
                        {t('landing.cta_existing')}{' '}
                        <Box
                            component="span"
                            onClick={() => navigate('/login')}
                            sx={{
                                color: '#c97ed9', fontWeight: 600, cursor: 'pointer',
                                textDecoration: 'underline', textDecorationColor: 'rgba(201,126,217,0.4)',
                                '&:hover': { color: '#e0aef0', textDecorationColor: '#e0aef0' },
                            }}
                        >
                            {t('landing.cta_existing_link')}
                        </Box>
                    </Typography>
                </Box>

                <Typography sx={{
                    fontSize: '0.78rem', color: 'rgba(255,255,255,0.3)',
                    animation: 'slide-up 1s ease forwards', animationDelay: '0.45s', opacity: 0,
                }}>
                    {t('landing.disclaimer')}
                </Typography>

                {/* Agent Demo Card */}
                <Box sx={{
                    mt: 8, width: '100%', maxWidth: 640,
                    animation: 'slide-up 1s ease forwards', animationDelay: '0.55s', opacity: 0,
                }}>
                    <AgentTypingDemo />
                </Box>

                {/* Scroll indicator */}
                <Box sx={{
                    position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    opacity: 0.4, animation: 'float 2s ease-in-out infinite',
                }}>
                    <Typography sx={{ fontSize: '0.65rem', letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                        {t('landing.scroll_more')}
                    </Typography>
                    <Box sx={{ width: 1, height: 40, background: 'linear-gradient(to bottom, rgba(112,18,128,0.8), transparent)' }} />
                </Box>
            </Box>

            {/* ===== STATS ===== */}
            <Box sx={{
                position: 'relative', zIndex: 1,
                py: { xs: 6, md: 10 }, px: { xs: 2, md: 6 },
                borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)',
                background: 'rgba(112,18,128,0.05)',
            }}>
                <Box sx={{
                    maxWidth: 1000, mx: 'auto',
                    display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    gap: 4, textAlign: 'center',
                }}>
                    {STATS.map((stat, i) => (
                        <Box key={i}>
                            <Typography sx={{
                                fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 900,
                                background: 'linear-gradient(135deg, #701280, #C850C0)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                lineHeight: 1, mb: 0.5,
                            }}>
                                {stat.value}
                            </Typography>
                            <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
                                {stat.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ===== HOW IT WORKS ===== */}
            <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 14 }, px: { xs: 2, md: 6 }, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#701280', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700, mb: 2 }}>
                    {t('landing.how_label')}
                </Typography>
                <Typography sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, letterSpacing: '-1px', mb: 2 }}>
                    {t('landing.how_title1')}<br />{t('landing.how_title2')}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', mb: 8, maxWidth: 500, mx: 'auto' }}>
                    {t('landing.how_subtitle')}
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, maxWidth: 900, mx: 'auto' }}>
                    {HOW_STEPS.map((item, i) => (
                        <Box key={i} sx={{
                            position: 'relative',
                            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '24px', p: { xs: 3, md: 4 }, textAlign: 'left',
                        }}>
                            <Typography sx={{ fontSize: '4rem', fontWeight: 900, color: 'rgba(112,18,128,0.55)', lineHeight: 1, mb: 1, fontFamily: 'monospace' }}>
                                {item.step}
                            </Typography>
                            <Typography sx={{ fontSize: '2rem', mb: 1.5 }}>{item.icon}</Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1, color: '#fff' }}>{item.title}</Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{item.desc}</Typography>
                            {i < 2 && (
                                <Box sx={{
                                    display: { xs: 'none', md: 'block' },
                                    position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)',
                                    fontSize: '1.5rem', color: 'rgba(112,18,128,0.5)', zIndex: 2,
                                }}>→</Box>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ===== FEATURES GRID ===== */}
            <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 12 }, px: { xs: 2, md: 6 }, background: 'rgba(0,0,0,0.3)' }}>
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#701280', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700, mb: 2 }}>
                        {t('landing.features_label')}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, letterSpacing: '-1px' }}>
                        {t('landing.features_title1')}<br />{t('landing.features_title2')}
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 2.5, maxWidth: 1100, mx: 'auto',
                }}>
                    {FEATURES.map((feature, i) => (
                        <FeatureCard key={i} feature={feature} delay={i * 80} />
                    ))}
                </Box>
            </Box>

            {/* ===== OLD CUSTOMERS BANNER ===== */}
            <Box sx={{
                position: 'relative', zIndex: 1,
                py: { xs: 6, md: 10 }, px: { xs: 2, md: 6 },
                background: 'linear-gradient(135deg, rgba(112,18,128,0.15) 0%, rgba(255,107,53,0.08) 100%)',
                borderTop: '1px solid rgba(112,18,128,0.2)', borderBottom: '1px solid rgba(112,18,128,0.2)',
            }}>
                <Box sx={{ maxWidth: 780, mx: 'auto', textAlign: 'center' }}>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 1,
                        background: 'rgba(255,107,53,0.15)', border: '1px solid rgba(255,107,53,0.3)',
                        borderRadius: '100px', px: 2, py: 0.75, mb: 3,
                    }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#FF6B35', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
                            {t('landing.old_badge')}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, letterSpacing: '-0.5px', mb: 2 }}>
                        {t('landing.old_title1')}<br />
                        <Box component="span" sx={{ color: '#701280' }}>{t('landing.old_title2')}</Box>
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '1rem', mb: 4, lineHeight: 1.7 }}>
                        {t('landing.old_desc')}
                    </Typography>
                    <Button
                        onClick={() => navigate('/login')}
                        variant="contained" size="large"
                        sx={{
                            background: 'linear-gradient(135deg, #701280, #C850C0)', color: '#fff',
                            textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                            borderRadius: '14px', px: 4, py: 1.5,
                            boxShadow: '0 8px 30px rgba(112,18,128,0.4)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #C850C0, #701280)',
                                boxShadow: '0 12px 40px rgba(112,18,128,0.6)', transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.25s ease',
                        }}
                    >
                        {t('landing.old_cta')}
                    </Button>
                </Box>
            </Box>

            {/* ===== FINAL CTA ===== */}
            <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 10, md: 16 }, px: { xs: 2, md: 6 }, textAlign: 'center' }}>
                <Box sx={{
                    position: 'absolute', inset: 0,
                    background: 'radial-gradient(ellipse at center, rgba(112,18,128,0.25) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#701280', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700, mb: 3, position: 'relative' }}>
                    {t('landing.final_label')}
                </Typography>
                <Typography sx={{
                    fontSize: { xs: '2.2rem', sm: '3rem', md: '4.5rem' },
                    fontWeight: 900, letterSpacing: '-1.5px', lineHeight: 1.05, mb: 3, position: 'relative',
                }}>
                    {t('landing.final_title1')}<br />
                    <Box component="span" sx={{
                        background: 'linear-gradient(135deg, #701280, #C850C0, #FF6B35)',
                        backgroundSize: '200% auto', WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent', animation: 'shimmer 4s linear infinite',
                    }}>
                        {t('landing.final_title2')}
                    </Box>
                </Typography>
                <Typography sx={{
                    color: 'rgba(255,255,255,0.5)', fontSize: '1rem', mb: 6,
                    maxWidth: 480, mx: 'auto', lineHeight: 1.7, position: 'relative',
                }}>
                    {t('landing.final_subtitle')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', position: 'relative' }}>
                    <Button
                        onClick={() => navigate('/registration')}
                        variant="contained" size="large"
                        sx={{
                            background: 'linear-gradient(135deg, #701280, #C850C0)', color: '#fff',
                            textTransform: 'none', fontWeight: 700, fontSize: '1.05rem',
                            borderRadius: '14px', px: 5, py: 1.8,
                            boxShadow: '0 8px 40px rgba(112,18,128,0.5)', animation: 'pulse-glow 3s infinite',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #C850C0, #701280)',
                                transform: 'translateY(-3px)', boxShadow: '0 16px 60px rgba(112,18,128,0.7)',
                            },
                            transition: 'all 0.25s ease',
                        }}
                    >
                        {t('landing.final_cta')}
                    </Button>
                    <Button
                        onClick={() => navigate('/login')}
                        size="large"
                        sx={{
                            color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)',
                            textTransform: 'none', fontWeight: 600, fontSize: '1.05rem',
                            borderRadius: '14px', px: 4, py: 1.8,
                            background: 'rgba(255,255,255,0.03)',
                            '&:hover': { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.25)' },
                            transition: 'all 0.25s ease',
                        }}
                    >
                        {t('landing.final_login')}
                    </Button>
                </Box>
                <Typography sx={{ mt: 3, fontSize: '0.78rem', color: 'rgba(255,255,255,0.25)', position: 'relative' }}>
                    {t('landing.final_footer')}
                </Typography>
            </Box>

            {/* ===== FOOTER ===== */}
            <Box sx={{
                borderTop: '1px solid rgba(255,255,255,0.06)',
                py: 4, px: { xs: 2, md: 6 },
                display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center', justifyContent: 'space-between', gap: 2,
                position: 'relative', zIndex: 1,
            }}>
                <img src={ADMIND_LOGO} alt="Admind" style={{ height: 28 }} />
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
                    © 2026 Admind · Ads that think for you
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    {footerLinks.map(link => (
                        <Typography key={link} sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', '&:hover': { color: 'rgba(255,255,255,0.6)' } }}>
                            {link}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
